// <JIM GLUE>
import type {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
} from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import { WEB3_CONFIG } from '../lib';
import assetFields from '../lib/graph/fragments/assetFields';
import listingFields from '../lib/graph/fragments/listingFields';
import { useWeb3State } from '.';

interface PaginationProps {
  step?: number;
  first?: number;
}

export const BONDED_ADDRESS = window.location.href.includes(
  'https://film3.mogulproductions.com/',
)
  ? '0xac0d675bada85ba4043236d1ae14e5b45c7a707b'
  : '0x75048438bc411c77Ae8d47a737E5B6E211223091';

export const featureBondedQuery = gql`
  ${assetFields}
  ${listingFields}
  query Asset {
    assets(
      orderBy: tokenId
      orderDirection: asc
      where: {
        id_in: [
          "0xac0d675bada85ba4043236d1ae14e5b45c7a707b/1"
          "0xac0d675bada85ba4043236d1ae14e5b45c7a707b/2"
          "0xac0d675bada85ba4043236d1ae14e5b45c7a707b/3"
          "0xac0d675bada85ba4043236d1ae14e5b45c7a707b/4"
        ]
      }
    ) {
      ...AssetField
      #activeListings(orderBy: starsPrice, orderDirection: asc) {
      #  ...ListingField
      #}
    }
  }
`;

export const useBondedScroll = <TData = any, TVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  initData: TData,
  merge: (curr: TData) => (prev: TData) => TData,
  hasEnded: (data: TData) => boolean,
  options?: QueryHookOptions<TData, TVariables> | undefined,
  paginationOptions?: PaginationProps | undefined,
) => {
  const { walletDetails } = useWeb3State();
  const [skip, setSkip] = useState(0);
  const [first, _] = useState(paginationOptions?.first ?? 20);
  const [ended, setEnded] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchedData, setFetchedData] = useState<TData>(initData);

  const queryData = useQuery<TData, TVariables>(query, {
    context: {
      forceFetch: true,
      bonded: true,
      queryDeduplication: false,
    },
    fetchPolicy: 'no-cache',
    ...options,
  });

  useEffect(() => {
    if (!queryData.data) {
      return;
    }

    setFetchedData(queryData.data);
    setSkip(0);
    setEnded(false);
  }, [queryData.data]);

  useEffect(() => {
    if (ended || !skip) {
      return;
    }

    void (async () => {
      const { data: newData } = await queryData.fetchMore({
        variables: { skip },
      });

      if (hasEnded(newData as TData)) {
        setEnded(true);
        setSkip(0);
      } else {
        setFetchedData(merge(newData as TData));
      }
      setFetching(false);
    })();
  }, [skip]);

  useEffect(() => {
    if (!fetching) {
      return;
    }

    setSkip(prev => prev + first);
  }, [fetching]);

  const isBsc = walletDetails.chain.id === WEB3_CONFIG.chains.bsc.id;

  return isBsc
    ? { ...queryData, fetchedData }
    : { fetchedData: { assets: [] } };
};
// </JIM GLUE>
